import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, GitCompareArrows, Plus } from "lucide-react";
import { DataSourceBadge } from "../DataSourceBadge";
import { Sparkline } from "../Sparkline";
import { formatINR, formatPercent, signColor } from "../../lib/format";

// Premium company intelligence header — identity, latest verified price and
// a compact intelligence stack, built entirely from the existing /companies/:ticker payload.
export const CompanyHero = ({ c, onAddToPortfolio }) => {
  const up = c.change_percent >= 0;
  const metadata = c.market_data_metadata || c.metadata;
  const lastVerified = metadata?.last_updated
    ? new Date(metadata.last_updated).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : null;

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
      <div className="mb-3 flex items-center justify-between">
        <Link to="/companies" data-testid="company-back-link" className="inline-flex items-center gap-1.5 font-mono text-xs text-zinc-500 transition-colors duration-200 hover:text-zinc-300">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to companies
        </Link>
        <Link
          to={`/compare?a=${c.ticker}`}
          data-testid="company-compare-cta"
          className="lv-press inline-flex items-center gap-1.5 rounded-md border border-white/[0.1] bg-[#0B0C0E] px-3 py-1.5 font-mono text-xs text-zinc-300 transition-colors hover:border-amber-500/40 hover:text-amber-200"
        >
          <GitCompareArrows className="h-3.5 w-3.5" /> Compare
        </Link>
      </div>

      <div className="lv-panel lv-top-edge relative overflow-hidden p-6 sm:p-8">
        <div
          className="pointer-events-none absolute -top-24 right-[-4rem] h-64 w-64 rounded-full opacity-[0.10] blur-3xl"
          style={{ background: up ? "#10B981" : "#F43F5E" }}
        />

        <div className="relative grid grid-cols-1 gap-6 lg:grid-cols-[1.3fr_1fr_0.85fr] lg:items-center">
          {/* Identity */}
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span data-testid="company-header-ticker" className="rounded border border-white/12 bg-[#0B0C0E] px-2 py-1 font-mono text-sm font-bold text-white">{c.ticker}</span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{c.exchange}</span>
              <span className="rounded border border-white/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-zinc-500">{c.cap_label}</span>
            </div>
            <h1 className="mt-2.5 text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-[2.25rem]">{c.name}</h1>
            <p className="mt-1.5 text-sm text-zinc-400">
              {c.sector}
              {c.industry && <><span className="text-zinc-600"> · </span>{c.industry}</>}
            </p>
          </div>

          {/* Price */}
          <div className="lg:border-l lg:border-white/[0.07] lg:pl-6">
            <div className="kicker mb-1.5">Latest Verified Price</div>
            <div data-testid="company-header-price" className="font-mono text-3xl font-bold tabular-nums text-white sm:text-4xl">{formatINR(c.price)}</div>
            <div className={`mt-1 font-mono text-sm ${signColor(c.change_percent)}`}>
              {formatPercent(c.change_percent)} <span className="text-zinc-500">today</span>
            </div>
            <div className="mt-3 h-10 w-full max-w-[180px]">
              <Sparkline data={c.sparkline} color={up ? "#10B981" : "#F43F5E"} height={40} />
            </div>
          </div>

          {/* Intelligence stack */}
          <div className="flex flex-col gap-2.5 lg:border-l lg:border-white/[0.07] lg:pl-6">
            {c.market_cap && (
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Market Cap</span>
                <span className="font-mono text-sm font-semibold tabular-nums text-white">{c.market_cap}</span>
              </div>
            )}
            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Data Source</span>
              <span data-testid="company-header-market-data-badge"><DataSourceBadge metadata={metadata} /></span>
            </div>
            {lastVerified && (
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Last Verified</span>
                <span className="font-mono text-xs text-zinc-400">{lastVerified}</span>
              </div>
            )}
            <button
              data-testid="company-add-to-portfolio"
              onClick={onAddToPortfolio}
              className="lv-press mt-1 inline-flex items-center justify-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 font-mono text-xs font-medium text-amber-300 transition-colors hover:bg-amber-500/20"
            >
              <Plus className="h-3.5 w-3.5" /> Portfolio simulator
            </button>
          </div>
        </div>

        <div className="relative mt-5 rounded-md border border-white/[0.06] bg-[#0B0C0E] px-3 py-2">
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Status · </span>
          <span className="text-xs text-zinc-400">Market prices are end-of-day development data. Research context is educational, not investment advice.</span>
        </div>
      </div>
    </motion.div>
  );
};
